import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useCreditsContext } from '../../../context/CreditsContext';
import { useUserPlan } from '../../../hooks/useUserPlan';
import AdForgeLogo from '../../../components/feature/AdForgeLogo';

const navSections = [
  {
    label: 'WORKSPACE',
    items: [
      { icon: 'ri-layout-grid-line', label: 'Dashboard', href: '/dashboard' },
      { icon: 'ri-building-2-line', label: 'Business Profiles', href: '/dashboard/profiles' },
      { icon: 'ri-flashlight-fill', label: 'Generate Ads', href: '/dashboard/generate' },
      { icon: 'ri-sparkling-2-fill', label: 'Social Posts', href: '/dashboard/social-post' },
      { icon: 'ri-movie-2-line', label: 'Video Ads', href: '/dashboard/video-ads' },
      { icon: 'ri-sound-module-line', label: 'Audio Studio', href: '/dashboard/audio-studio' },
      { icon: 'ri-edit-box-line', label: 'Ad Editor', href: '/dashboard/editor' },
      { icon: 'ri-export-line', label: 'Export', href: '/dashboard/export' },
      { icon: 'ri-history-line', label: 'History', href: '/dashboard/history' },
    ],
  },
  {
    label: 'ACCOUNT',
    items: [
      { icon: 'ri-bank-card-line', label: 'Billing', href: '/dashboard/billing' },
      { icon: 'ri-customer-service-2-line', label: 'Contact Us', href: '/contact' },
      { icon: 'ri-settings-3-line', label: 'Settings', href: '/dashboard/settings' },
    ],
  },
];

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  onMobileClose?: () => void;
}

const PLAN_LABELS: Record<string, string> = {
  free: 'Free Plan',
  pro: 'Pro Plan',
  agency: 'Agency Plan',
};

export default function Sidebar({ collapsed, onToggle, onMobileClose }: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { creditsRemaining, creditLimit, creditPercent, daysUntilReset, loading: creditsLoading } = useCreditsContext();
  const { plan, loading: planLoading } = useUserPlan();

  const displayName = (user?.user_metadata?.full_name as string | undefined) ?? user?.email?.split('@')[0] ?? 'User';
  const avatarLetter = displayName.charAt(0).toUpperCase();
  const email = user?.email ?? '';

  const handleLogout = async () => {
    await signOut();
    navigate('/login');
  };

  const isActive = (href: string) =>
    href === '/dashboard' ? location.pathname === '/dashboard' : location.pathname.startsWith(href);

  const planLabel = PLAN_LABELS[plan] ?? 'Free Plan';

  return (
    <aside
      className={`h-screen bg-stone-950 flex flex-col transition-all duration-300 shrink-0 ${collapsed ? 'w-16' : 'w-60'}`}
    >
      {/* Header */}
      <div className={`flex items-center h-16 px-4 border-b border-stone-800/60 shrink-0 ${collapsed ? 'justify-center' : 'justify-between'}`}>
        {!collapsed && (
          <Link to="/" className="flex items-center gap-2">
            <AdForgeLogo size="md" light={true} />
          </Link>
        )}
        <div className="flex items-center gap-1">
          {/* Mobile close button */}
          <button
            onClick={onMobileClose}
            className="md:hidden w-7 h-7 flex items-center justify-center rounded-md text-stone-500 hover:text-white hover:bg-stone-800 transition-all duration-200 cursor-pointer"
            aria-label="Close menu"
          >
            <i className="ri-close-line text-base" />
          </button>
          {/* Desktop collapse toggle */}
          <button
            onClick={onToggle}
            className="hidden md:flex w-7 h-7 items-center justify-center rounded-md text-stone-500 hover:text-white hover:bg-stone-800 transition-all duration-200 cursor-pointer"
          >
            <i className={`ri-${collapsed ? 'menu-unfold' : 'menu-fold'}-line text-base`} />
          </button>
        </div>
      </div>

      {/* Plan badge */}
      {!collapsed && !planLoading && (
        <div className="mx-3 mt-3 bg-gradient-to-r from-orange-500/15 to-amber-500/5 border border-orange-500/20 rounded-xl px-3 py-2.5">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-bold text-orange-400">{planLabel}</span>
            {plan === 'free' && (
              <Link to="/pricing" className="text-xs text-orange-400/70 hover:text-orange-400 transition-colors whitespace-nowrap">
                Upgrade
              </Link>
            )}
          </div>
          {!creditsLoading && (
            <>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs text-stone-400">
                  <span className="text-white font-semibold">{creditsRemaining}</span>
                  <span className="text-stone-500"> / {creditLimit} credits</span>
                </span>
                <span className="text-xs text-stone-600">{daysUntilReset}d left</span>
              </div>
              <div className="w-full h-1.5 bg-stone-800 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${
                    creditPercent > 30
                      ? 'bg-gradient-to-r from-orange-500 to-amber-400'
                      : creditPercent > 10
                      ? 'bg-amber-500'
                      : 'bg-red-500'
                  }`}
                  style={{ width: `${creditPercent}%` }}
                />
              </div>
              {creditsRemaining <= 0 && (
                <div className="mt-1.5 text-xs text-red-400 font-semibold">No credits left</div>
              )}
            </>
          )}
        </div>
      )}

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-3 px-2">
        {navSections.map((section) => (
          <div key={section.label} className="mb-4">
            {!collapsed && (
              <div className="px-3 mb-1.5 text-xs font-semibold tracking-widest text-stone-600">
                {section.label}
              </div>
            )}
            {section.items.map((item) => {
              const active = isActive(item.href);
              return (
                <Link
                  key={item.label}
                  to={item.href}
                  onClick={onMobileClose}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl mb-0.5 transition-all duration-200 ${
                    active
                      ? 'bg-orange-500/15 text-orange-400'
                      : 'text-stone-400 hover:text-white hover:bg-stone-800/60'
                  }`}
                >
                  <div className="w-5 h-5 flex items-center justify-center shrink-0">
                    <i className={`${item.icon} text-base`} />
                  </div>
                  {!collapsed && (
                    <span className="text-sm font-medium flex-1">{item.label}</span>
                  )}
                  {!collapsed && active && (
                    <div className="w-1.5 h-1.5 rounded-full bg-orange-400 shrink-0" />
                  )}
                </Link>
              );
            })}
          </div>
        ))}
      </nav>

      {/* User */}
      <div className={`border-t border-stone-800/60 p-3 shrink-0 ${collapsed ? 'flex justify-center' : ''}`}>
        <div className={`flex items-center gap-3 ${collapsed ? '' : 'w-full'}`}>
          <div className="w-8 h-8 flex items-center justify-center rounded-full bg-gradient-to-br from-orange-500 to-amber-400 text-white text-xs font-bold shrink-0">
            {avatarLetter}
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <div className="text-xs font-semibold text-white truncate">{displayName}</div>
              <div className="text-xs text-stone-500 truncate">{email}</div>
            </div>
          )}
          {!collapsed && (
            <button
              onClick={handleLogout}
              className="w-6 h-6 flex items-center justify-center text-stone-500 hover:text-red-400 transition-colors cursor-pointer"
              title="Sign out"
            >
              <i className="ri-logout-box-r-line text-sm" />
            </button>
          )}
        </div>
      </div>
    </aside>
  );
}
